import { Link, Stack, Typography } from '@mui/material';
import { Button } from 'components/Button/Button';
import React, { FunctionComponent } from 'react';
import { SvgIcon } from 'components/SvgIcon/SvgIcon';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { getContactLinkDataWithDefault } from 'utils';
import { ContactLink } from 'features/auth/types';
import { safeFormatToNational } from 'utils/phoneNumberUtils/phoneNumberUtils';

interface BulletPointProps {
  icon: FunctionComponent;
  text: string;
}

const BulletPoint = ({ icon, text }: BulletPointProps) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={2}
    className="pending-approval-step__bullet-point"
    role="listitem"
  >
    <SvgIcon
      component={icon}
      inheritViewBox
      color="primary"
      sx={{ fontSize: 48 }}
      aria-hidden="true"
    />
    <Typography variant="body1" color="grey.800">
      {text}
    </Typography>
  </Stack>
);

export interface PendingApprovalStepProps {
  image: React.ReactNode;
  title: string;
  description: string;
  buttonTitle?: string;
  bulletPoints?: BulletPointProps[];
  contactLink?: ContactLink;
  onContinue?: () => void;
}

export const PendingApprovalStep = ({
  image,
  title,
  description,
  buttonTitle,
  bulletPoints,
  contactLink,
  onContinue,
}: PendingApprovalStepProps) => {
  const { translation } = useTranslationContext();

  const contactLinkData = getContactLinkDataWithDefault(contactLink);

  return (
    <>
      <Stack
        alignItems="center"
        maxWidth={{ xs: '280px', sm: '540px' }}
        marginTop={6}
        className="pending-approval-step"
      >
        <div className="pending-approval-step__image" aria-hidden="true">
          {image}
        </div>
        <Typography
          variant="title2"
          color="primary"
          textAlign="center"
          marginTop={{ xs: 4, md: 6 }}
          className="pending-approval-step__title"
        >
          {title}
        </Typography>
        <Typography
          variant="callout"
          color="grey.800"
          textAlign="center"
          marginTop={{ xs: 2, md: 3 }}
          className="pending-approval-step__description"
        >
          {description}
        </Typography>
        {buttonTitle && (
          <Button
            variant="primary"
            size="xlarge"
            fullWidth
            sx={{ marginTop: { xs: 10, md: 13 }, marginBottom: 3 }}
            onClick={onContinue}
            className="pending-approval-step__button"
            aria-label={buttonTitle}
          >
            {buttonTitle}
          </Button>
        )}
        {bulletPoints && (
          <Stack
            spacing={3}
            marginTop={{ xs: 3, md: 5 }}
            marginBottom={3}
            className="pending-approval-step__bullet-points"
            role="list"
          >
            {bulletPoints.map((point, index) => (
              <BulletPoint key={index} icon={point.icon} text={point.text} />
            ))}
          </Stack>
        )}
      </Stack>
      {contactLink && (
        <Typography
          variant="body1"
          color="grey.800"
          textAlign="center"
          marginTop="auto"
          paddingBottom={6}
          className="pending-approval-step__contact-link"
        >
          {translation('caregiver_questions_contact_the_care_team')}
          {': '}
          <Link
            href={contactLinkData.url}
            underline="none"
            color="primary"
            aria-label={translation('caregiver_questions_contact_the_care_team')}
          >
            {safeFormatToNational(contactLinkData.displayValue)}
          </Link>
        </Typography>
      )}
    </>
  );
};
